import { useCallback, useRef, useState } from 'react';

/**
 * Custom hook to manage drag-and-drop functionality for a carousel component.
 *
 * @param {React.RefObject<HTMLDivElement>} containerRef - Ref to the carousel container element.
 * @param {boolean} canScroll - Indicates if the carousel can scroll.
 * @returns {object} - An object containing drag state and handlers for drag events.
 */
const useCarouselDrag = (containerRef: React.RefObject<HTMLDivElement | null>, canScroll: boolean) => {
	const [isMouseDown, setIsMouseDown] = useState(false);
	const [isDragging, setIsDragging] = useState(false);

	const mouseCoordinates = useRef({ startX: 0, scrollLeft: 0 });

	const handleDragStart = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			const slider = containerRef.current;
			if (!slider || !canScroll) return;

			event.preventDefault();
			const startX = event.pageX - slider.offsetLeft;
			mouseCoordinates.current = { startX, scrollLeft: slider.scrollLeft };
			setIsMouseDown(true);
		},
		// The warning is generally safe to ignore in this case (because useRef is stable by default)
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[canScroll]
	);

	const handleDrag = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			const slider = containerRef.current;
			if (!isMouseDown || !slider || !canScroll) return;

			event.preventDefault();
			const x = event.pageX - slider.offsetLeft;
			const travelX = x - mouseCoordinates.current.startX;
			const scrollDistance = mouseCoordinates.current.scrollLeft - travelX;
			slider.scrollLeft = scrollDistance;

			if (Math.abs(travelX) > 10) {
				setIsDragging(true);
			}
		},
		// The warning is generally safe to ignore in this case (because useRef is stable by default)
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[canScroll, isMouseDown]
	);

	const handleDragEnd = useCallback(() => {
		setIsMouseDown(false);
		setIsDragging(false);
	}, []);

	return {
		isDragging,
		handleDragStart,
		handleDrag,
		handleDragEnd
	};
};

export default useCarouselDrag;
